import { T } from '@design/index';
import type { DoctorPublicProfile, MarketplaceProvider, ProviderAccreditation } from '../../types';

/**
 * Everything a patient wants to know before choosing a doctor, and every bit
 * of it from the server.
 *
 * The profile page used to show a name, a specialty and a phone number,
 * because that is all the directory row carries. The doctor's own record —
 * what they trained in, how long they have practised, the council they are
 * registered with, what a consultation costs, where they sit — has been on
 * the backend the whole time and never reached this app.
 *
 * Two rules run through these blocks:
 *
 *  • A field nobody filled in is not drawn. No "20 minutes", no
 *    "Mon–Fri 09:00–18:00" for a doctor who published no hours.
 *  • A registration NUMBER appears only once verification has passed, and
 *    when it is withheld the screen says why. The server decides that, not
 *    this component.
 */

const MODE_LABEL: Record<string, string> = {
  IN_CLINIC: 'Clinic visit',
  ONLINE: 'Video consultation',
};

const DAY_LABEL: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const rupees = (n: number) => `₹${n.toLocaleString('en-IN')}`;

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{ padding: '20px 16px 10px' }}>
      <h2 style={{ fontFamily: T.font, fontSize: 14, fontWeight: 800, color: T.text, margin: 0, letterSpacing: '-0.01em' }}>
        {title}
      </h2>
      <div style={{ width: 28, height: 2.5, background: T.teal, borderRadius: 2, marginTop: 5 }} />
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: `1px solid ${T.borderLight}` }}>
      <span style={{ fontFamily: T.font, fontSize: 12.5, color: T.textSec, flexShrink: 0 }}>{label}</span>
      <span style={{ fontFamily: T.font, fontSize: 12.5, color: T.text, textAlign: 'right', fontWeight: 600 }}>{children}</span>
    </div>
  );
}

function Chip({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'good' | 'warn' | 'muted' }) {
  const colors = {
    good: { fg: '#047857', bg: '#ECFDF5', bd: '#A7F3D0' },
    warn: { fg: '#B45309', bg: '#FFFBEB', bd: '#FDE68A' },
    muted: { fg: T.navy, bg: `${T.navy}08`, bd: `${T.navy}18` },
  }[tone];
  return (
    <span style={{
      fontFamily: T.font, fontSize: 11, fontWeight: 700, color: colors.fg,
      background: colors.bg, border: `1px solid ${colors.bd}`, borderRadius: 20, padding: '4px 10px',
      display: 'inline-block',
    }}>
      {children}
    </span>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, lineHeight: 1.7, margin: '10px 0 0' }}>
      {children}
    </p>
  );
}

/* ── Medical registration & compliance ─────────────────────────────────── */

export function RegistrationCard({ doctor, providerVerified }: { doctor: DoctorPublicProfile; providerVerified: boolean }) {
  const reg = doctor.registration;
  const verified = reg.verified && providerVerified;

  return (
    <>
      <SectionHeader title="Medical registration & compliance" />
      <div style={{ padding: '0 16px' }}>
        <Card>
          <div style={{ display: 'flex', gap: 11, alignItems: 'center', paddingBottom: 11 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11, flexShrink: 0,
              background: (verified ? T.success : T.warning) + '14',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={verified ? T.success : T.warning} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
                {verified && <path d="M9 12l2 2 4-4" />}
              </svg>
            </div>
            <div>
              <div style={{ fontFamily: T.font, fontSize: 13.5, fontWeight: 800, color: T.text }}>
                Registered medical practitioner
              </div>
              <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, marginTop: 1 }}>
                {verified
                  ? 'Registration verified by NeoHealthCard'
                  : 'Awaiting NeoHealthCard verification'}
              </div>
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 4 }}>
            <Row label="Medical registration no.">
              {reg.number ?? (
                <span style={{ fontWeight: 400, color: T.textSec }}>
                  {reg.numberWithheldReason ?? 'Not published'}
                </span>
              )}
            </Row>
            {reg.council && <Row label="Medical council">{reg.council}</Row>}
            <Row label="Registration status">
              <Chip tone={verified ? 'good' : 'warn'}>
                {verified ? 'Verified' : 'Verification pending'}
              </Chip>
            </Row>
            {reg.validUntil && <Row label="Valid until">{reg.validUntil}</Row>}
            <Row label="Verified by">NeoHealthCard</Row>
          </div>

          {/* What was actually checked, each from its own record — rather than
              one badge standing in for three different claims. */}
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              ['Identity', reg.status.toLowerCase() === 'verified'],
              ['Medical registration', reg.verified],
              ['Practice location', doctor.clinics.some((c) => c.verified)],
            ].map(([label, ok]) => (
              <div key={String(label)} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{
                  width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
                  background: ok ? `${T.success}18` : `${T.textTer}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke={ok ? T.success : T.textTer} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    {ok ? <path d="M20 6L9 17l-5-5" /> : <path d="M12 6v8M12 18h.01" />}
                  </svg>
                </span>
                <span style={{ fontFamily: T.font, fontSize: 12, color: ok ? T.text : T.textSec }}>
                  {label} {ok ? 'verified' : 'not verified yet'}
                </span>
              </div>
            ))}
          </div>

          <Note>
            {verified
              ? 'Medical registration verified by NeoHealthCard. NeoHealthCard facilitates discovery and booking and does not practise medicine — consultations, prescriptions and referrals are issued solely at the doctor’s clinical discretion.'
              : 'This doctor’s medical registration has not yet been verified by NeoHealthCard, so the registration number is not published. NeoHealthCard facilitates discovery and booking and does not practise medicine.'}
          </Note>
        </Card>
      </div>
    </>
  );
}

/* ── Services offered ──────────────────────────────────────────────────── */

export function ServicesCard({
  doctor, doctorName, onBook,
}: {
  doctor: DoctorPublicProfile;
  doctorName: string;
  onBook: (mode: 'IN_CLINIC' | 'ONLINE') => void;
}) {
  const modes = doctor.consultation.modes;
  const clinic = doctor.clinics[0];
  // Nothing published means nothing is known — both are offered and the
  // booking step shows what openings actually exist.
  const shown = modes.length > 0 ? modes : ['IN_CLINIC', 'ONLINE'];

  const feeFor = (m: string) => (m === 'ONLINE' ? doctor.consultation.videoFee : doctor.consultation.clinicFee);
  const whereFor = (m: string) =>
    m === 'ONLINE'
      ? 'Secure online consultation'
      : clinic
        ? [clinic.name, clinic.city].filter(Boolean).join(', ')
        : 'At the clinic';

  return (
    <>
      <SectionHeader title="Services offered" />
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shown.map((m) => {
          const fee = feeFor(m);
          return (
            <div key={m} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontFamily: T.font, fontSize: 13.5, fontWeight: 800, color: T.text }}>
                    {MODE_LABEL[m] ?? m}
                  </div>
                  <div style={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: T.teal, marginTop: 1 }}>
                    {m === 'ONLINE' ? 'Telemedicine' : 'Clinic · In-person'}
                  </div>
                </div>
                <div style={{ fontFamily: T.font, fontSize: 13.5, fontWeight: 800, color: T.text, whiteSpace: 'nowrap' }}>
                  {fee ? rupees(fee) : 'Quoted'}
                </div>
              </div>
              <div style={{ fontFamily: T.font, fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.5 }}>
                {m === 'ONLINE'
                  ? `Secure online consultation with ${doctorName}.`
                  : `In-person consultation with ${doctorName}.`}
              </div>
              <div style={{ fontFamily: T.font, fontSize: 10.5, color: T.textTer, marginTop: 8 }}>
                {whereFor(m)}
              </div>
              <button
                onClick={() => onBook(m as 'IN_CLINIC' | 'ONLINE')}
                style={{
                  marginTop: 12, width: '100%', fontFamily: T.font, fontSize: 12.5, fontWeight: 700,
                  color: T.white, background: m === 'ONLINE' ? T.teal : T.navy,
                  border: 'none', borderRadius: 10, padding: '10px 0', cursor: 'pointer',
                }}
              >
                Book {MODE_LABEL[m]?.toLowerCase() ?? 'consultation'}
              </button>
            </div>
          );
        })}
        {modes.length === 0 && (
          <Note>
            This doctor has not published which kinds of consultation they offer, so both are shown. The booking step
            shows the openings they have actually published.
          </Note>
        )}
        {doctor.consultation.audioFee && (
          <Note>Audio consultation {rupees(doctor.consultation.audioFee)} · booked at the clinic.</Note>
        )}
        <Note>
          Prescriptions, investigations, referrals and certificates are issued only when clinically appropriate,
          following the doctor’s assessment.
        </Note>
      </div>
    </>
  );
}

/* ── About, training, languages ────────────────────────────────────────── */

export function AboutCard({ doctor }: { doctor: DoctorPublicProfile }) {
  const hasAny = doctor.bio || doctor.qualifications.length > 0 || doctor.languages.length > 0 || doctor.experienceYears;
  if (!hasAny) return null;

  return (
    <>
      <SectionHeader title="About this doctor" />
      <div style={{ padding: '0 16px' }}>
        <Card>
          {doctor.bio && (
            <p style={{ fontFamily: T.font, fontSize: 12.5, color: T.textSec, lineHeight: 1.7, margin: 0 }}>
              {doctor.bio}
            </p>
          )}

          {doctor.qualifications.length > 0 && (
            <div style={{ marginTop: doctor.bio ? 14 : 0 }}>
              <div style={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 800, color: T.textTer, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Qualifications
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {doctor.qualifications.map((q) => <Chip key={q}>{q}</Chip>)}
              </div>
            </div>
          )}

          {doctor.languages.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 800, color: T.textTer, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Speaks
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {doctor.languages.map((l) => <Chip key={l}>{l}</Chip>)}
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

/* ── Practice locations ────────────────────────────────────────────────── */

export function ClinicsCard({ doctor }: { doctor: DoctorPublicProfile }) {
  if (doctor.clinics.length === 0) return null;

  return (
    <>
      <SectionHeader title={doctor.clinics.length > 1 ? 'Practice locations' : 'Where this doctor sits'} />
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {doctor.clinics.map((c) => {
          const days = DAY_ORDER.filter((d) => c.operatingHours?.[d]);
          return (
            <Card key={c.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: T.font, fontSize: 13.5, fontWeight: 800, color: T.text }}>{c.name}</span>
                {c.verified && <Chip tone="good">Verified</Chip>}
              </div>
              {(c.address || c.city) && (
                <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
                  {[c.address, c.city, c.state, c.pincode].filter(Boolean).join(', ')}
                </div>
              )}
              {c.phone && (
                <a href={`tel:${c.phone}`} style={{ fontFamily: T.font, fontSize: 12, color: T.navy, textDecoration: 'none', display: 'inline-block', marginTop: 6 }}>
                  {c.phone}
                </a>
              )}

              {c.facilities.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {c.facilities.map((f) => <Chip key={f}>{f}</Chip>)}
                </div>
              )}

              {days.length > 0 && (
                <div style={{ marginTop: 12, borderTop: `1px solid ${T.borderLight}`, paddingTop: 6 }}>
                  {days.map((d) => (
                    <Row key={d} label={DAY_LABEL[d] ?? d}>
                      {String(c.operatingHours[d]).toLowerCase() === 'closed'
                        ? <span style={{ fontWeight: 400, color: T.textSec }}>Closed</span>
                        : String(c.operatingHours[d]).replace('-', ' – ')}
                    </Row>
                  ))}
                  <Note>
                    The clinic’s stated hours. The openings you can actually book are the ones above, published by the
                    doctor.
                  </Note>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
}

/* ── Accreditations (any provider) ─────────────────────────────────────── */

export function AccreditationsCard({ rows }: { rows: ProviderAccreditation[] }) {
  if (rows.length === 0) return null;
  return (
    <>
      <SectionHeader title="Accreditations" />
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((a) => (
          <Card key={`${a.body}-${a.number ?? ''}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>{a.body}</span>
              {a.expired
                ? <Chip tone="warn">Lapsed</Chip>
                : <Chip tone={a.verified ? 'good' : 'muted'}>{a.verified ? 'Verified' : 'Self-declared'}</Chip>}
            </div>
            <div style={{ fontFamily: T.font, fontSize: 11.5, color: T.textSec, marginTop: 3 }}>
              {[a.number ? `No. ${a.number}` : null, a.validUntil ? `valid to ${a.validUntil.slice(0, 10)}` : null]
                .filter(Boolean).join(' · ') || 'No certificate details recorded'}
            </div>
          </Card>
        ))}
        <Note>
          “Verified” means NeoHealthCard checked the certificate against the issuing body. “Self-declared” means the
          provider stated it and nobody here has checked.
        </Note>
      </div>
    </>
  );
}

/* ── Frequently asked questions ────────────────────────────────────────── */

/**
 * Logistical questions only — never clinical advice.
 *
 * These are app copy, the same for every doctor, exactly as the pharmacy and
 * doctor apps carry them. Two of the four are written from this doctor's own
 * record, so a doctor who does not consult online is not told they can be
 * booked for a video call.
 */
export function FaqCard({ doctor, doctorName }: { doctor: DoctorPublicProfile; doctorName: string }) {
  const offersVideo = doctor.consultation.modes.length === 0 || doctor.consultation.modes.includes('ONLINE');
  const clinic = doctor.clinics[0];

  const faqs: [string, string][] = [
    [
      'What should I bring to my visit?',
      'Previous reports and your current medicines list. You can also share records securely from NeoHealthCard once the appointment is booked.',
    ],
    [
      'How early should I arrive?',
      clinic
        ? `Arrive about 10 minutes before your slot for check-in at ${clinic.name}.`
        : 'Arrive about 10 minutes before your slot for check-in.',
    ],
    [
      'How are consultation fees handled?',
      doctor.consultation.clinicFee
        ? `The clinic consultation is ${rupees(doctor.consultation.clinicFee)}, settled at the clinic. NeoHealthCard does not take an online consultation payment for doctors.`
        : 'Fees are settled at the clinic. NeoHealthCard does not take an online consultation payment for doctors.',
    ],
    offersVideo
      ? ['Can I consult online instead?', `Yes — book the video consultation to see ${doctorName} securely from home.`]
      : ['Can I consult online instead?', `${doctorName} has not published online consultations, so appointments are in clinic.`],
  ];

  return (
    <>
      <SectionHeader title="Frequently asked questions" />
      <div style={{ padding: '0 16px' }}>
        <Card>
          {faqs.map(([q, a], i) => (
            <div key={q} style={{ paddingTop: i === 0 ? 0 : 10, marginTop: i === 0 ? 0 : 10, borderTop: i === 0 ? 'none' : `1px solid ${T.borderLight}` }}>
              <div style={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 800, color: T.text }}>{q}</div>
              <div style={{ fontFamily: T.font, fontSize: 11.5, color: T.textSec, marginTop: 3, lineHeight: 1.6 }}>{a}</div>
            </div>
          ))}
        </Card>
      </div>
    </>
  );
}

/* ── Footer: who provides the care ─────────────────────────────────────── */

export function ProvidedByFooter({ provider }: { provider: MarketplaceProvider }) {
  return (
    <div style={{ padding: '20px 16px 0' }}>
      <div style={{ background: `${T.navy}05`, border: `1px solid ${T.navy}18`, borderRadius: 12, padding: '13px 16px' }}>
        <p style={{ fontFamily: T.font, fontSize: 11.5, color: T.textSec, margin: 0, lineHeight: 1.7 }}>
          Healthcare service provided by <strong style={{ color: T.text }}>{provider.name}</strong>. Technology,
          discovery and booking are facilitated through <strong style={{ color: T.navy }}>NeoHealthCard</strong>,
          which does not practise medicine.
        </p>
        <p style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, margin: '8px 0 0', lineHeight: 1.7 }}>
          NeoHealthCard does not publish a doctor’s personal phone number, email, or social handles. Once your
          appointment is booked you can message securely here, and every message stays linked to that appointment.
        </p>
      </div>
    </div>
  );
}
